/**
 * Holly's bell — the one small brass sound that first makes company dangerous
 * and eventually means she is still there.
 */

import { connectChain, envelope, filter, gain, noiseSource, softClip } from '../synth';
import { BASE_EMISSION_RADIUS_METRES } from './foley';
import type { SoundDefinition } from '../types';

export const HOLLY_BELL_SOUND = 'holly-bell';
/** Metres of Holly's travel between strokes. A walking sheep's stride. */
export const HOLLY_BELL_STRIDE_METRES = 0.62;
export const BELL_EMISSION_MULTIPLIER = 1.35;

/**
 * Playback gain, DERIVED from the emission radius rather than chosen by ear.
 *
 * `docs/audio-direction.md`: *"if grating sounds loud but emits like straw, the
 * stealth fairness rule breaks"*. That rule has only ever been enforced between
 * the three footfall surfaces, which all play at one gain, so comparing their
 * rendered peaks was enough. The bell plays at its own gain, so the quantity
 * that has to obey the rule is the peak **in the mix** — the definition's peak
 * plus this gain — and that is what `audio.spec.ts` now asserts.
 *
 * The arithmetic, so the next person can redo it rather than trust it:
 *
 *   concrete trot emits 11.90 m and plays at 0.85 (`footfallGain`)
 *   the bell emits      18.90 m, a ratio of 1.588 = +4.0 dB
 *   footfall-concrete renders at -25.8 dBFS peak -> -27.2 dBFS in the mix
 *   so the bell belongs at about -23.2 dBFS in the mix
 *
 * **The first version of this shipped at 0.78 against a definition rendering at
 * -7.5 dBFS peak**, which put the bell 17.6 dB over a trot instead of 4 — the
 * loudest thing in the game, twice a second, in a room built out of quiet. It
 * passed every threshold in the gate, because no gate compared it to anything.
 */
export const HOLLY_BELL_PLAYBACK_GAIN = 0.56;

/**
 * How far Holly's bell carries, as a multiple of the footfall baseline.
 *
 * The measured ladder is straw-creep 1.26 m (Rosalie at her quietest),
 * straw-trot 6.30 m, concrete-trot 11.90 m (normal hard-floor travel), Holly's
 * bell 18.90 m, concrete-roll 21.42 m, and grating-roll 34.02 m. Its meaning is
 * the Beat 9 cost: travelling with Holly is louder than travelling alone. The
 * multiplier matches grating because a struck brass shell carries like it.
 */
export function bellEmissionRadius(): number {
  return BASE_EMISSION_RADIUS_METRES * BELL_EMISSION_MULTIPLIER;
}

const MODES = [
  { frequency: 611, level: 0.085, decay: 0.95 },
  { frequency: 1132, level: 0.300, decay: 0.78 },
  { frequency: 1138, level: 0.255, decay: 0.75 },
  { frequency: 1709, level: 0.190, decay: 0.34 },
  { frequency: 2343, level: 0.130, decay: 0.22 },
  { frequency: 3011, level: 0.095, decay: 0.15 },
  { frequency: 4087, level: 0.062, decay: 0.095 },
  { frequency: 5310, level: 0.038, decay: 0.055 },
] as const;

/**
 * One struck mode: near-instant attack, exponential decay, silent thereafter.
 *
 * `pulse()` in dog.ts/out.ts/weather.ts ramps linearly, which is right for a
 * panel contact and wrong for a bell — a linear tail sounds like a fade, an
 * exponential one like something ringing. `exponentialRampToValueAtTime` cannot
 * reach or pass through zero, so the floor is followed by an explicit zero;
 * without it the render tail is a DC-ish hum that the gate reads as offset.
 */
function ring(shape: GainNode, t0: number, level: number, decay: number): void {
  shape.gain.setValueAtTime(0, t0);
  shape.gain.linearRampToValueAtTime(level, t0 + 0.0015);
  shape.gain.exponentialRampToValueAtTime(level * 0.0005, t0 + 0.0015 + decay);
  shape.gain.setValueAtTime(0, t0 + 0.0015 + decay);
}

function mode(ctx: BaseAudioContext, out: AudioNode, frequency: number, level: number, decay: number): void {
  const oscillator = ctx.createOscillator();
  oscillator.type = 'sine';
  oscillator.frequency.value = frequency;
  const shape = gain(ctx, 0);
  ring(shape, ctx.currentTime, level, decay);
  oscillator.connect(shape);
  shape.connect(out);
  oscillator.start(0);
}

/**
 * One stroke, parameterised by what is touching the brass. The three voices
 * below are the same bell — the same eight modes, the same crack — and differ
 * only in what damps it, which is the leitmotif's whole arc (spec §8:
 * liability, comfort, the last sound in the game) said in one function.
 */
interface StrikeSpec {
  /** Multiplies every mode's decay: < 1 is damped, > 1 rings on. */
  readonly decayScale: number;
  /** The top of the passband. A plush mouth takes the upper partials first. */
  readonly lowpass: number;
  readonly duration: number;
  readonly release: number;
  readonly master: number;
  /** The strike's own noise: a clapper on brass, or felt on brass. */
  readonly transient: { readonly level: number; readonly decay: number; readonly frequency: number; readonly q: number };
}

function strike(ctx: BaseAudioContext, spec: StrikeSpec): void {
  const master = gain(ctx, spec.master);
  connectChain(
    master,
    filter(ctx, { type: 'lowpass', frequency: spec.lowpass, q: 0.6 }),
    filter(ctx, { type: 'highpass', frequency: 300, q: 0.7 }),
    softClip(ctx, 1.4),
    envelope(ctx, spec.duration, 0.001, spec.release),
    ctx.destination,
  );
  for (const partial of MODES) mode(ctx, master, partial.frequency, partial.level, partial.decay * spec.decayScale);
  const transient = gain(ctx, 0);
  ring(transient, ctx.currentTime, spec.transient.level, spec.transient.decay);
  connectChain(
    noiseSource(ctx, spec.duration, 613),
    filter(ctx, { type: 'bandpass', frequency: spec.transient.frequency, q: spec.transient.q }),
    transient,
    master,
  );
}

const hollyBell: SoundDefinition = {
  id: HOLLY_BELL_SOUND,
  label: "Holly's bell",
  description: 'One stroke of a small cast-brass sheep bell, cracked and wool-damped.',
  category: 'foley',
  duration: 1.1,
  loop: false,
  render(ctx) {
    // 0.10 puts the rendered peak in the same band as the rest of the foley
    // (-18 dBFS, beside `hens-panic` and `landing-thud`) rather than 18 dB above
    // it. The gameplay loudness lives in `HOLLY_BELL_PLAYBACK_GAIN`, which is
    // where it can be derived; a definition that is hot on its own is a trap for
    // the two later scenes that play this same bell with a different intent.
    strike(ctx, {
      decayScale: 1, lowpass: 5200, duration: 1.1, release: 0.06, master: 0.10,
      transient: { level: 0.16, decay: 0.028, frequency: 2600, q: 1.15 },
    });
  },
};

/**
 * The bell in her mouth (beat 14's lift and four steps). A plush jaw round the
 * crown: the upper partials go first, every mode dies in a third of its time,
 * and the strike is felt on brass rather than a clapper — a soft low knock
 * with the ring behind it. Still unmistakably the bell; the player has heard
 * it for five beats.
 */
const hollyBellMuffled: SoundDefinition = {
  id: 'holly-bell-muffled',
  label: "Holly's bell — in her mouth",
  description: 'The same stroke, damped by a plush mouth round the crown: short, low, close.',
  category: 'foley',
  duration: 0.8,
  loop: false,
  render(ctx) {
    strike(ctx, {
      decayScale: 0.34, lowpass: 2300, duration: 0.8, release: 0.08, master: 0.075,
      transient: { level: 0.22, decay: 0.05, frequency: 520, q: 0.8 },
    });
  },
};

/**
 * THE LAST SOUND IN THE GAME (spec §8, G4). The cut to black lands on it and
 * nothing follows it.
 *
 * It is the undamped bell: her step swings it free of her lips and it rings
 * the way it never could on Holly, whose wool held it. Every mode decays three
 * times as long as the everyday stroke, so the 1132/1138 Hz pair has time to
 * beat — the slow waver that makes a real bell sound like one object and not
 * a chord — and the tail runs out to silence inside the black. Measured by
 * `check:audio` like everything else; the ring-out is the gate's
 * `tailSilence`, and the judgement is D-207's.
 */
const hollyBellLast: SoundDefinition = {
  id: 'holly-bell-last',
  label: "Holly's bell — the last stroke",
  description: 'One free stroke, rung out to silence: the last sound in the game.',
  category: 'foley',
  duration: 5,
  loop: false,
  render(ctx) {
    strike(ctx, {
      decayScale: 3.2, lowpass: 5600, duration: 5, release: 0.4, master: 0.10,
      transient: { level: 0.14, decay: 0.03, frequency: 2600, q: 1.15 },
    });
  },
};

export const bellSounds: readonly SoundDefinition[] = [hollyBell, hollyBellMuffled, hollyBellLast];
